import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1651331189447-704ac3643c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBjb3Jwb3JhdGUlMjBidWlsZGluZ3xlbnwwfHx8fDE3NzgxMzQ5Mzl8MA&ixlib=rb-4.1.0&q=85";

export const SplitAuthLayout = ({ children, tagline }) => {
  const { language, setLanguage, t } = useLanguage();
  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-white">
      <div className="relative hidden md:block">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 brand-gradient-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="text-xs text-white/55">{t("footer_internal_use")}</div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-end p-4 sm:p-6">
          <div
            className="flex items-center rounded-md border border-slate-200 overflow-hidden text-sm"
            data-testid="auth-language-toggle"
          >
            <button
              type="button"
              onClick={() => setLanguage("en")}
              className={`px-3 py-1.5 transition-colors focus-ring ${
                language === "en"
                  ? "bg-[#0A2540] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="auth-language-toggle-en"
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLanguage("zh")}
              className={`px-3 py-1.5 transition-colors focus-ring ${
                language === "zh"
                  ? "bg-[#0A2540] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="auth-language-toggle-zh"
            >
              中文
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 pb-10">
          <div className="w-full max-w-md animate-fade-in">{children}</div>
        </div>
      </div>
    </div>
  );
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email.trim(), password);
      if (user.must_change_password) {
        navigate("/profile?force=1", { replace: true });
        return;
      }
      const home = user.role === "admin" ? "/admin" : user.role === "reviewer" ? "/reviewer" : "/agent";
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "Pending approval") setError(t("err_pending_approval"));
      else if (detail === "Account disabled") setError(t("err_disabled"));
      else if (detail === "Invalid email or password") setError(t("err_invalid_credentials"));
      else setError(detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <SplitAuthLayout>
      <div className="space-y-2 mb-8">
        <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
          {t("login_heading")}
        </h2>
        <p className="text-slate-500 text-sm">{t("login_subheading")}</p>
      </div>

      <form className="space-y-5" onSubmit={onSubmit} data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-[#0A2540] text-sm font-medium">
            {t("login_email")}
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
              placeholder="you@company.com"
              data-testid="login-email-input"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-[#0A2540] text-sm font-medium">
            {t("login_password")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>
        </div>

        {error ? (
          <div
            className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2"
            data-testid="login-error"
          >
            {error}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-11 bg-[#0A2540] hover:bg-[#051626] text-white font-medium"
          data-testid="login-submit-button"
        >
          {submitting ? "..." : t("login_submit")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <div className="flex items-center gap-3">
          <div className="h-px bg-slate-200 flex-1" />
          <span className="text-xs text-slate-400 uppercase tracking-wider">{t("login_or")}</span>
          <div className="h-px bg-slate-200 flex-1" />
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={onGoogle}
          className="w-full h-11 border-slate-200 text-[#0A2540] hover:bg-slate-50 font-medium"
          data-testid="login-google-button"
        >
          <GoogleIcon />
          <span className="ml-2">{t("login_google")}</span>
        </Button>

        <p className="text-sm text-slate-500 text-center">
          {t("login_no_account")}{" "}
          <Link to="/register" className="text-[#007AFF] hover:underline font-medium" data-testid="link-to-register">
            {t("login_to_register")}
          </Link>
        </p>
      </form>
    </SplitAuthLayout>
  );
};

export default Login;
